import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    // -------------------------------------------------------------------------
    // ENLUAZE / PERFORMANCE (jul-2026)
    // Original: as sources eram atribuídas e videoElement.load() era chamado
    // imediatamente no connectedCallback. Como a home tem vídeos de fundo em
    // seções bem abaixo da dobra, isso baixava ~1,2 MB de vídeo antes de o
    // cliente ver qualquer coisa, competindo com o banner principal (o LCP).
    // Agora o vídeo só começa a carregar quando a seção se aproxima da tela.
    // A imagem de preview continua aparecendo normalmente até lá.
    // -------------------------------------------------------------------------
    if (!('IntersectionObserver' in window)) {
      this.#loadVideo();
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.#loadVideo();
        this.#observer?.disconnect();
        this.#observer = null;
      },
      // Começa a carregar um pouco antes de entrar na tela, para não haver
      // atraso perceptível quando o cliente chega na seção.
      { rootMargin: '300px 0px' }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    this.#observer?.disconnect();
    this.#observer = null;
  }

  #loadVideo() {
    const { videoSources, videoElement } = this.refs;
    if (!videoElement || videoElement.dataset.enluazeLoaded === 'true') return;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.dataset.enluazeLoaded = 'true';
    videoElement.load();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
